import * as React from 'react';

import {
  SizeContextProvider,
  type SizeContextSizeValue,
  useSizeContext,
} from './SizeContext';

import type {Theme} from './Theme';
export type {Theme};

import designerTheme from './designer';
import adminTheme from './admin';
export {default as designerTheme} from './designer';

import {getDisplayName} from './utils';

const InternalThemeContext: React.Context<Theme> =
  React.createContext(designerTheme);
InternalThemeContext.displayName = 'ThemeContext';
export const ThemeContext: React.Context<Theme> =
  Object.create(InternalThemeContext);

const xLargeThemes = [adminTheme.name];

const useLegacyThemeSize = (theme: Theme): SizeContextSizeValue => {
  /**
   * To provide the new SizeContext (theming layer) with awareness of whether
   * it is in designer (medium) or admin (x-large).
   *
   * Respects parent SizeContext if already set, otherwise falls back to
   * theme-name-based detection for legacy compatibility.
   *
   * @see https://webflow.atlassian.net/wiki/spaces/FOUN/pages/46333964/Theming+-+Colors+x+Sizing
   */
  const parentSize = useSizeContext().size;

  // If there's already a parent size context that's not the default, respect it
  if (parentSize && parentSize !== 'medium') {
    return parentSize;
  }

  // Otherwise, use theme-name-based detection as fallback
  const {name} = theme;
  const isXLarge = xLargeThemes.includes(name);
  const size = isXLarge ? 'x-large' : 'medium';

  return size as SizeContextSizeValue;
};

const EnvironmentStyles = ({styles}: {styles: string}) => {
  const allEnvironmentsStyles = (
    <style>{`* { box-sizing: border-box; }`}</style>
  );

  return (
    <>
      {allEnvironmentsStyles}
      {styles ? <style>{styles}</style> : null}
    </>
  );
};

type ThemeContextProviderProps = {
  children: React.ReactNode;
  value: Theme;
};
const ThemeContextProvider = ({children, value}: ThemeContextProviderProps) => {
  const themeSize = useLegacyThemeSize(value);

  return (
    <>
      <EnvironmentStyles styles={value.globalStyleTagStyles} />
      <InternalThemeContext.Provider value={value}>
        <SizeContextProvider size={themeSize}>{children}</SizeContextProvider>
      </InternalThemeContext.Provider>
    </>
  );
};

// @ts-expect-error - TS2741 - Property '$$typeof' is missing in type '({ children, value }: { children: any; value: any; }) => Element' but required in type 'ProviderExoticComponent<ProviderProps<Theme>>'.
ThemeContext.Provider = ThemeContextProvider;

export const useTheme = (): Theme => React.useContext(InternalThemeContext);

export type PropsWithTheme<Props> = Props & {theme: Theme};

/*
 * Enhances passed in component by attaching the “theme” from context as prop.
 *
 * Component -> Component
 *
 * Forwards refs to the wrapped Component so consumers (e.g. `tooltipTrigger`
 * via `cloneElement({ref})`) can reach the underlying DOM node. For the ref
 * to actually land on a DOM element, the wrapped Component should itself be
 * a `React.forwardRef` component. Components that aren't forwardRef silently
 * drop the ref (pre-existing behavior) and React emits its usual warning.
 */
export function themeable<Props>(
  Component: React.ComponentType<PropsWithTheme<Props>>
): React.ComponentType<React.PropsWithRef<Props>> {
  const NewComponent = React.memo(
    React.forwardRef<any, Props>((props: Props, ref) => (
      <Component {...props} theme={useTheme()} ref={ref} />
    ))
  );
  // @ts-expect-error - TS2345 - Argument of type 'AbstractComponent<Props, any>' is not assignable to parameter of type 'AbstractComponent<any, any>'.
  NewComponent.displayName = `themeable(${getDisplayName(Component)})`;
  // Cast to the public return type: the internal `memo(forwardRef(...))`
  // result is structurally compatible but TS's generic inference for
  // `PropsWithRef<Props>` vs `PropsWithoutRef<Props> & RefAttributes<any>`
  // doesn't converge without a constraint that would be overly restrictive
  // for existing callers.
  return NewComponent as unknown as React.ComponentType<
    React.PropsWithRef<Props>
  >;
}
